const _excluded = ["label", "items", "isRequired", "textFieldProps", "sx", "disabled", "renderSX", "labelSX", "inputSX", "textFieldSX"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import "core-js/modules/es.regexp.to-string.js";
import "core-js/modules/es.symbol.description.js";
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { forwardRef } from 'react';
import PropTypes, { checkPropTypes } from 'prop-types';
import { Box, TextField, Autocomplete, InputLabel } from '@mui/material';
import RequiredIndicator from "./RequiredIndicator.js";
import { object } from 'yup';

/**
 * Wrapper of the Mui Autocomplete component
 * Prevent closing by clicking outside of the dialog by NOT passing in a handleClose prop
 * We need to forward the ref to the Autocomplete component for use with react-hook-form
 * https://reactjs.org/docs/forwarding-refs.html
 * renderSX is styling for the surrounding box
 * sx is styling for the Autocomplete component
 * labelSX is styling for the label
 * textFieldSX is styling for the text field this is another MUI wrapper contains an input and the dropdown icon
 * inputLabelSX is styling for the input inside the textField
 * @returns
 * @param {object} props
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Typeahead = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      label,
      items,
      isRequired,
      textFieldProps,
      sx,
      disabled,
      renderSX,
      labelSX,
      inputSX,
      textFieldSX
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  // Override the default Autocomplete getOptionLabel / getOptionSelected methods
  // We can override the override methods by passing in the same method name as a prop

  // See: https://material-ui.com/api/autocomplete/#getoptionlabel-item
  const getOptionLabel = option => {
    const foundOpt = getOpObj(option);
    // We need to return an empty string for the label for the place holder to correctly show
    return (foundOpt === null || foundOpt === void 0 ? void 0 : foundOpt.label) || (foundOpt === null || foundOpt === void 0 ? void 0 : foundOpt.name) || '';
  };

  // See: https://material-ui.com/api/autocomplete/#isOptionEqualToValue-item
  const isOptionEqualToValue = (option, value) => {
    /*
      There always must be a found option. In the event nothing matches one of our options
      we have to return true. This will "select" the placeholder / null option
    */
    if (value === '' || value === null || value === undefined) {
      return true;
    }
    const foundOpt = getOpObj(value);

    // Things will get strange if we don't have a found option at this point and you dun goofed A A Ron
    const isEqual = foundOpt ? (option === null || option === void 0 ? void 0 : option.id) === (foundOpt === null || foundOpt === void 0 ? void 0 : foundOpt.id) || (option === null || option === void 0 ? void 0 : option.value) === (foundOpt === null || foundOpt === void 0 ? void 0 : foundOpt.value) : true;
    return isEqual;
  };

  /**
   * Helper method to get the option object can either be an object or just the value of the id
   * @param {*} option
   * @returns
   */
  const getOpObj = option => {
    if (!option.id && !option.value) {
      option = items.find(op => {
        var _op$id, _option, _option$value, _option2;
        const optValue = (op === null || op === void 0 ? void 0 : (_op$id = op.id) === null || _op$id === void 0 ? void 0 : _op$id.toString()) || ((_option = option) === null || _option === void 0 ? void 0 : (_option$value = _option.value) === null || _option$value === void 0 ? void 0 : _option$value.toString());
        return optValue === ((_option2 = option) === null || _option2 === void 0 ? void 0 : _option2.toString());
      });
    }
    return option;
  };
  return /*#__PURE__*/_jsx(Autocomplete, _objectSpread({
    ref: ref,
    sx: sx || {
      width: 300
    },
    options: items,
    autoHighlight: true,
    disabled: disabled,
    getOptionLabel: getOptionLabel,
    isOptionEqualToValue: isOptionEqualToValue,
    renderOption: (props, option) => /*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({
      component: "li"
    }, props), {}, {
      children: option.label
    })),
    renderInput: params => {
      return /*#__PURE__*/_jsxs(Box, {
        sx: renderSX || {},
        children: [/*#__PURE__*/_jsxs(InputLabel, {
          error: textFieldProps === null || textFieldProps === void 0 ? void 0 : textFieldProps.error,
          htmlFor: "typeahead-input",
          sx: labelSX || {},
          children: [/*#__PURE__*/_jsx(RequiredIndicator, {
            isRequired: isRequired
          }), label || 'Search']
        }), /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread(_objectSpread({}, params), textFieldProps), {}, {
          sx: textFieldSX || {},
          inputProps: _objectSpread(_objectSpread({}, params.inputProps), {}, {
            sx: inputSX || {},
            autoComplete: 'new-password' // disable autocomplete and autofill
          })
        }))]
      });
    }
    // Forward the rest of the props to the Autocomplete component
    // https://material-ui.com/api/autocomplete/#props
  }, props));
});

// Set the displayName to make it easier to identify in the React DevTools
// This needs to be set explicity because the forwardRef is used
// https://reactjs.org/docs/react-component.html#displayname
Typeahead.displayName = 'Typeahead';
Typeahead.propTypes = {
  disabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  items: PropTypes.array,
  label: PropTypes.string,
  sx: PropTypes.object,
  renderSX: PropTypes.object,
  labelSX: PropTypes.object,
  textFieldSX: PropTypes.object,
  inputSX: PropTypes.object,
  textFieldProps: PropTypes.object
};
export default Typeahead;